import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { AgentConfigService } from '../agent-config/agent-config.service';

/**
 * Agent Feature Detection Service
 * 
 * Detects and manages agent feature availability by checking if:
 * - Agent configuration file exists
 * - Agents folder structure is present
 * 
 * This service helps gracefully handle environments where agent features
 * may not be available or enabled.
 * 
 * Usage:
 * - Call initialize() during app startup
 * - Use isAvailable() to check feature status
 * - Subscribe to agentsAvailable$ for reactive updates
 */
@Injectable({
  providedIn: 'root'
})
export class AgentFeatureService {
  
  private readonly agentsAvailable = new BehaviorSubject<boolean>(false);
  public agentsAvailable$ = this.agentsAvailable.asObservable();
  
  private initialized = false;

  constructor(
    private readonly logger: LoggerService,
    private readonly agentConfigService: AgentConfigService
  ) {}

  /**
   * Initialize and check if agents feature is available
   * 
   * Attempts to load agent config file to determine feature availability.
   * Should be called during app initialization.
   * 
   * @returns Promise<boolean> - True if agents are available
   */
  async initialize(): Promise<boolean> {
    try {
      if (this.initialized) {
        return this.agentsAvailable.value;
      }

      // Try to load agent config through centralized service
      await this.agentConfigService.loadAgentConfig();
      
      // Check if agents were loaded successfully
      const agents = this.agentConfigService.getAllAgents();
      const isAvailable = agents.length > 0;
      
      this.agentsAvailable.next(isAvailable);
      this.initialized = true;
      
      this.logger.logInfo(`[AgentFeature] Agents available: ${isAvailable} (${agents.length} agents)`, 'initialize');
      
      return isAvailable;
    } catch (error: any) {
      this.logger.logInfo('[AgentFeature] Agents feature not available', 'initialize', error);
      this.agentsAvailable.next(false);
      this.initialized = true;
      return false;
    }
  }

  /**
   * Check if agents feature is currently available
   * 
   * @returns boolean - True if agents folder and config exist
   */
  isAvailable(): boolean {
    try {
      return this.agentsAvailable.value;
    } catch (error: any) {
      this.logger.logInfo('[AgentFeature] Error checking availability', 'isAvailable', error);
      return false;
    }
  }

  /**
   * Get observable for reactive updates on feature availability
   * 
   * @returns Observable<boolean> - Stream of availability status
   */
  getAvailability(): Observable<boolean> {
    try {
      return this.agentsAvailable$;
    } catch (error: any) {
      this.logger.logInfo('[AgentFeature] Error getting availability observable', 'getAvailability', error);
      return new BehaviorSubject<boolean>(false).asObservable();
    }
  }

  /**
   * Manually set availability (for testing or override)
   * 
   * @param available - Availability status to set
   */
  setAvailability(available: boolean): void {
    try {
      this.agentsAvailable.next(available);
    } catch (error: any) {
      this.logger.logInfo('[AgentFeature] Error setting availability', 'setAvailability', error);
    }
  }
}

